/**
 * Shared workflow-state components.
 *
 * Every intelligence-backed section of the UI must be able to say which of
 * these it is in — LOADING, EMPTY, FEATURE DISABLED, NO ACCESS, FAILED,
 * PARTIAL DATA, UNKNOWN — instead of rendering an ambiguous blank. The
 * backend distinguishes these precisely (503 not_enabled vs 403 vs 404 vs
 * empty list); these components keep that distinction visible.
 */
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Loader2,
  PowerOff,
  Lock,
  AlertTriangle,
  RefreshCw,
  Inbox,
  HelpCircle,
  Ticket,
} from "lucide-react";

/** Inline loading row for a section. */
export const SectionLoading = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground text-sm">
    <Loader2 className="w-4 h-4 animate-spin" />
    <span>{label}</span>
  </div>
);

/** Deliberate emptiness — the call succeeded and there is genuinely nothing. */
export const SectionEmpty = ({ icon: Icon = Inbox, title, hint, action }) => (
  <div className="flex flex-col items-center gap-2 py-8 text-center">
    <Icon className="w-8 h-8 text-muted-foreground/50" />
    <p className="text-sm font-medium text-foreground">{title}</p>
    {hint && <p className="text-xs text-muted-foreground max-w-sm">{hint}</p>}
    {action}
  </div>
);

/**
 * The backend answered 503 not_enabled: the capability exists but this
 * environment has not switched it on. Not an error, not emptiness.
 */
export const FeatureDisabled = ({ feature, hint }) => (
  <div className="flex flex-col items-center gap-2 py-8 text-center">
    <PowerOff className="w-8 h-8 text-muted-foreground/50" />
    <p className="text-sm font-medium text-foreground">{feature} is not enabled</p>
    <p className="text-xs text-muted-foreground max-w-sm">
      {hint ||
        "This capability is built but switched off for this environment. An administrator can enable it in the backend configuration."}
    </p>
  </div>
);

/** Compact pill for summary cards whose data source is switched off. */
export const DisabledPill = ({ label = "Not enabled" }) => (
  <Badge
    variant="outline"
    className="bg-muted/50 text-muted-foreground border-border gap-1 font-normal"
  >
    <PowerOff className="w-3 h-3" />
    {label}
  </Badge>
);

/**
 * The tenant has not been granted this surface (403 tenant_not_entitled).
 * Distinct from FEATURE DISABLED (the platform flag is off for everyone) and
 * from NO ACCESS (this user's role) — no role change fixes it, and the user
 * has done nothing wrong.
 */
export const NotEntitled = ({ feature, message }) => (
  <div className="flex flex-col items-center gap-2 py-8 text-center">
    <Ticket className="w-8 h-8 text-muted-foreground/50" />
    <p className="text-sm font-medium text-foreground">
      {feature ? `${feature} is not part of your plan yet` : 'Not included in your plan yet'}
    </p>
    <p className="text-xs text-muted-foreground max-w-sm">
      {message ||
        'Your organization has not been granted this capability. It is enabled by the OMNΛS platform team, not from inside the product.'}
    </p>
  </div>
);

/** Compact pill for summary rows whose source the tenant is not entitled to. */
export const NotEntitledPill = ({ label = 'Not in plan' }) => (
  <Badge
    variant="outline"
    className="bg-muted/50 text-muted-foreground border-border gap-1 font-normal"
  >
    <Ticket className="w-3 h-3" />
    {label}
  </Badge>
);

/** Caller's role cannot see or do this. */
export const NoAccess = ({ message }) => (
  <div className="flex flex-col items-center gap-2 py-8 text-center">
    <Lock className="w-8 h-8 text-muted-foreground/50" />
    <p className="text-sm font-medium text-foreground">You don't have access to this</p>
    <p className="text-xs text-muted-foreground max-w-sm">
      {message || "Your role does not include this capability. Ask an administrator if you need it."}
    </p>
  </div>
);

/** A real failure, with retry. Pass the classified error from classifyWorkflowError. */
export const SectionError = ({ error, onRetry, label }) => (
  <div className="flex flex-col items-center gap-2 py-8 text-center">
    <AlertTriangle className="w-8 h-8 text-red-400/70" />
    <p className="text-sm font-medium text-foreground">{label || "This section failed to load"}</p>
    <p className="text-xs text-muted-foreground max-w-sm break-words">
      {error?.message || "Unexpected error."}
    </p>
    {onRetry && (
      <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
        Retry
      </Button>
    )}
  </div>
);

/**
 * Some sections of a composite view are unavailable. Names which, and why,
 * so partial data is never mistaken for the whole picture.
 */
export const PartialDataNotice = ({ unavailable }) => {
  if (!unavailable || unavailable.length === 0) return null;
  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardContent className="py-3 px-4 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Partial data. </span>
          {unavailable.map((u, i) => (
            <span key={u.name}>
              {u.name}{" "}
              {u.reason === "disabled"
                ? "is not enabled"
                : u.reason === "not-entitled"
                  ? "is not part of your plan"
                  : u.reason === "no-access"
                    ? "is not visible to your role"
                    : "failed to load"}
              {i < unavailable.length - 1 ? "; " : "."}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * An honest unknown. Use wherever the backend returned null/UNKNOWN instead
 * of a value — never substitute a default.
 *
 * The reason WHY something is unknown is the operationally useful half ("no
 * source states this" vs "the record could not be read"), so it is exposed
 * to assistive technology rather than living only in a hover tooltip that a
 * keyboard or screen-reader user never reaches.
 */
export const UnknownValue = ({ reason, className = "" }) => {
  const explanation = reason || "The system does not know this. No default has been assumed.";
  return (
    <span
      className={`inline-flex items-center gap-1 text-muted-foreground ${className}`}
      title={explanation}
    >
      <HelpCircle className="w-3 h-3" aria-hidden="true" />
      Unknown
      <span className="sr-only"> — {explanation}</span>
    </span>
  );
};

/**
 * Route a classified error (from classifyWorkflowError) to the right state
 * component. `feature` names what was being loaded.
 */
export const ClassifiedSectionState = ({ classified, feature, onRetry, emptyFallback }) => {
  if (!classified) return emptyFallback || null;
  switch (classified.kind) {
    case "disabled":
      return <FeatureDisabled feature={feature} />;
    case "not-entitled":
      return <NotEntitled feature={feature} message={classified.message} />;
    case "no-access":
      return <NoAccess message={classified.message} />;
    case "not-found":
      return (
        <SectionEmpty
          title={`${feature} not found`}
          hint="The record may have been removed, or it belongs to a different tenant."
        />
      );
    default:
      return <SectionError error={classified} onRetry={onRetry} label={`${feature} failed to load`} />;
  }
};
